import axios from "axios";
import { useEffect, useRef } from "react";

import { API_URL } from "../constants";
import { authHeader } from "../utils/auth";

export const RENDER_POLL_MS = 3000;
// Stop polling after this long, so a slow/failed worker never leaves the page
// spinning forever — callers surface their own "taking longer than usual" message.
export const MAX_POLL_MS = 10 * 60 * 1000;

/** @param {unknown} error */
const terminalPollFailure = (error) => {
  const status = /** @type {{response?: {status?: unknown}}} */ (error)?.response?.status;
  if (status === 401) {
    return {
      code: "authentication_required",
      message: "Your session expired. Sign in again, then try again.",
      status,
    };
  }
  if (status === 403) {
    return {
      code: "access_denied",
      message:
        "You no longer have access to this listing prep. Sign in as the owner, then try again.",
      status,
    };
  }
  if (status === 404) {
    return {
      code: "report_job_not_found",
      message: "This update is no longer available. Refresh the page, then try again.",
      status,
    };
  }
  return null;
};

/**
 * Poll an owner-visible report job while `active`. Once that job completes,
 * fetch `GET /computations/:uuid/report-document/` until `isReady(data)` is
 * true, then stop both timers and hand the fresh document and completed job
 * (when present) to `onReady`. A
 * failed job stops immediately and calls `onFailed` with the API's safe
 * failure object. Older API responses do not include a job id; in that case we
 * retain the legacy document-only polling behavior. Transient GET errors keep
 * polling. If `maxMs` elapses first, polling stops and `onTimeout` fires.
 *
 * @param {{
 *   active: boolean,
 *   uuid: string,
 *   jobId?: string | null,
 *   isReady: (data: any, job?: any) => boolean,
 *   onReady: (data: any, job?: any) => void,
 *   onFailed?: (failure: {code?: string, message?: string} | null, job: any) => void,
 *   onTimeout?: () => void,
 *   pollMs?: number,
 *   maxMs?: number,
 * }} options
 */
const usePollReportDocument = ({
  active,
  uuid,
  jobId = null,
  isReady,
  onReady,
  onFailed,
  onTimeout,
  pollMs = RENDER_POLL_MS,
  maxMs = MAX_POLL_MS,
}) => {
  // The callbacks live in a ref so each poll tick sees the latest closures
  // (fresh state) without the effect — and its timers — restarting every render.
  const callbacksRef = useRef({ isReady, onReady, onFailed, onTimeout });
  callbacksRef.current = { isReady, onReady, onFailed, onTimeout };

  useEffect(() => {
    if (!active) return undefined;
    // The interval and timeout each need to clear the other, so both live on
    // one object the closures share.
    const timers = /** @type {{ handle?: any, timeout?: any }} */ ({});
    let requestInFlight = false;
    let stopped = false;
    const stop = () => {
      stopped = true;
      clearInterval(timers.handle);
      clearTimeout(timers.timeout);
    };
    const pollOnce = async () => {
      // A slow request must not create overlapping status/document reads.
      if (requestInFlight || stopped) return;
      requestInFlight = true;
      try {
        let completedJob = null;
        if (jobId) {
          const jobResp = await axios.get(`${API_URL}/computations/${uuid}/jobs/${jobId}/`, {
            headers: authHeader(),
          });
          if (stopped) return;
          const job = jobResp.data || {};
          if (job.status === "failed") {
            stop();
            if (callbacksRef.current.onFailed) {
              callbacksRef.current.onFailed(job.failure || null, job);
            }
            return;
          }
          if (job.status !== "completed") return;
          completedJob = job;
        }
        const resp = await axios.get(`${API_URL}/computations/${uuid}/report-document/`, {
          headers: authHeader(),
        });
        if (stopped) return;
        if (callbacksRef.current.isReady(resp.data, completedJob)) {
          stop();
          if (completedJob) callbacksRef.current.onReady(resp.data, completedJob);
          else callbacksRef.current.onReady(resp.data);
        }
      } catch (error) {
        const failure = terminalPollFailure(error);
        if (failure) {
          stop();
          if (callbacksRef.current.onFailed) callbacksRef.current.onFailed(failure, null);
        }
        // Network errors and 5xx responses remain transient and retry on the
        // next interval. Authentication/access/not-found responses are terminal.
      } finally {
        requestInFlight = false;
      }
    };
    // Check immediately when a job becomes active. Waiting a full interval
    // before the first status read adds avoidable latency to fast failures and
    // completions; the interval is only for subsequent checks.
    timers.handle = setInterval(pollOnce, pollMs);
    timers.timeout = setTimeout(() => {
      stop();
      if (callbacksRef.current.onTimeout) callbacksRef.current.onTimeout();
    }, maxMs);
    void pollOnce();
    return stop;
  }, [active, uuid, jobId, pollMs, maxMs]);
};

export default usePollReportDocument;
